import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { AdminService } from '@admin/services/admin.service';
import { IOption } from '@shared/models/icommons.model';
import { IUserPermission, ILocationPermission } from '@shared/models/iuser.model';
import { UntypedFormGroup, UntypedFormControl, UntypedFormBuilder } from '@angular/forms';
import { Subscription } from 'rxjs';

@Component({
  selector: 'miq-global-selection',
  templateUrl: './global-selection.component.html',
  styleUrls: ['./global-selection.component.scss'],
  standalone: false,
})
export class GlobalSelectionComponent implements OnInit {
  public globalSection: UntypedFormGroup;
  public searchPhrase: string;
  public globalRegionList: IOption[];
  public productTypeList: IOption[];
  public divisionList: IOption[];
  public marketList: IOption[];
  public subMarketList: IOption[];

  @Input() selectedPermissions: ILocationPermission;
  @Output() searchEmitter = new EventEmitter<any>();
  @Output() advancedSearchEmitter = new EventEmitter<any>();
  @Output() addPermissionEmitter = new EventEmitter<any>();

  // selection variables
  public selectedGlobalRegion: IOption;
  public selectedDivision: IOption;
  public selectedProductType: IOption;
  public selectedMarket: IOption;
  public selectedSubMarket: IOption;
  public userPermission: IUserPermission;

  constructor(
    private adminService: AdminService,
    private formBuilder: UntypedFormBuilder
  ) {}

  ngOnInit() {
    this.globalSection = this.formBuilder.group({
      globalRegionFC: new UntypedFormControl(),
      divisionFC: new UntypedFormControl(),
      marketFC: new UntypedFormControl(),
      submarketFC: new UntypedFormControl(),
      productTypeFC: new UntypedFormControl(),
    });

    // initalizing userPermissions and assigning values on selection
    this.selectedGlobalRegion = { key: -1, value: '' };
    this.selectedMarket = { key: -1, value: '' };
    this.selectedSubMarket = { key: -1, value: '' };
    this.selectedProductType = { key: -1, value: '' };
    this.selectedDivision = { key: -1, value: '' };

    this.userPermission = {
      globalRegion: this.selectedGlobalRegion,
      division: this.selectedDivision,
      market: this.selectedMarket,
      submarket: this.selectedSubMarket,
      productType: this.selectedProductType,
      status: { key: -1, value: '' },
    };

    if (this.selectedPermissions) {
      this.getUserPermissionPreference();
    }
    this.initializeDropDownValues();
    this.initializeFormControls();
  }

  public getUserPermissionPreference() {
    this.selectedGlobalRegion.key = this.selectedPermissions.globalRegionId;
    this.selectedGlobalRegion.value = this.selectedPermissions.globalRegionVal;
    this.selectedMarket.key = this.selectedPermissions.marketId;
    this.selectedMarket.value = this.selectedPermissions.marketVal;
    this.selectedSubMarket.key = this.selectedPermissions.subMarketId;
    this.selectedSubMarket.value = this.selectedPermissions.subMarketVal;
    this.selectedDivision.key = this.selectedPermissions.divisionId;
    this.selectedDivision.value = this.selectedPermissions.divisionVal;
    this.selectedProductType.key = this.selectedPermissions.propertyUsageTypeId;
    this.selectedProductType.value = this.selectedPermissions.propertyUsageTypeVal;
  }

  /*
   *Initialze form controls
   */
  public initializeFormControls() {
    this.globalSection.controls.globalRegionFC.valueChanges.subscribe(value => {
      if (value) {
        this.selectedGlobalRegion = value;
        this.getDivision(this.selectedGlobalRegion.key);
        this.userPermission.globalRegion = value;
        this.addPermissionEmitter.emit(this.userPermission);
        if (this.selectedGlobalRegion.key === 0) {
          this.globalSection.controls.divisionFC.disable();
        } else {
          this.globalSection.controls.divisionFC.enable();
        }
      }
    });

    this.globalSection.controls.divisionFC.valueChanges.subscribe(value => {
      if (this.globalSection.controls.divisionFC.dirty) {
        this.selectedPermissions = null;
      }
      if (value) {
        this.selectedDivision = value;
        this.getMarkets(this.selectedDivision.key);
        this.userPermission.division = value;
        this.addPermissionEmitter.emit(this.userPermission);
        if (this.selectedDivision.key === 0) {
          this.globalSection.controls.marketFC.disable();
        } else {
          this.globalSection.controls.marketFC.enable();
        }
      }
    });

    this.globalSection.controls.marketFC.valueChanges.subscribe(value => {
      if (this.globalSection.controls.marketFC.dirty) {
        this.selectedPermissions = null;
      }
      if (value) {
        this.selectedMarket = value;
        this.getSubMarkets(this.selectedMarket.key);
        this.userPermission.market = value;
        this.addPermissionEmitter.emit(this.userPermission);
        if (this.selectedMarket.key === 0) {
          this.globalSection.controls.submarketFC.disable();
        } else {
          this.globalSection.controls.submarketFC.enable();
        }
      }
    });

    this.globalSection.controls.submarketFC.valueChanges.subscribe(value => {
      if (this.globalSection.controls.submarketFC.dirty) {
        this.selectedPermissions = null;
      }
      if (value) {
        this.selectedSubMarket = value;
        this.userPermission.submarket = value;
        this.addPermissionEmitter.emit(this.userPermission);
        if (this.selectedSubMarket && this.selectedSubMarket.key === 0) {
          this.advancedSearchEmitter.emit('close');
        }
      }
    });

    this.globalSection.controls.productTypeFC.valueChanges.subscribe(value => {
      if (this.globalSection.controls.marketFC.dirty) {
        this.selectedPermissions = null;
      }
      if (value) {
        this.selectedProductType = value;
        this.userPermission.productType = value;
        this.addPermissionEmitter.emit(this.userPermission);
      }
    });
  }

  /**
   * gets the values for drop downs
   */
  public initializeDropDownValues() {
    this.adminService.getPropertyUsageTypes().subscribe(result => {
      this.productTypeList = result;
      if (this.selectedPermissions) {
        this.globalSection.controls.productTypeFC.setValue(
          result.find(f => f.key === this.selectedPermissions.propertyUsageTypeId)
        );
        this.userPermission.productType = result.find(f => f.key === this.selectedPermissions.propertyUsageTypeId);
      } else {
        this.selectedProductType = result[0];
        this.userPermission.productType = result[0];
        this.globalSection.controls.productTypeFC.setValue(result[0]);
        // this.addPermissionEmitter.emit(this.userPermission);
      }
    });

    this.getGlobalRegions();
    this.addPermissionEmitter.emit(this.userPermission);
  }

  // advanced search event emitter
  public advancedSearch() {
    this.advancedSearchEmitter.emit(this.selectedSubMarket);
  }

  public get globalRegionFC() {
    return this.globalSection.get('globalRegionFC');
  }
  public get regionFC() {
    return this.globalSection.get('regionFC');
  }
  public get marketFC() {
    return this.globalSection.get('marketFC');
  }
  public get submarketFC() {
    return this.globalSection.get('submarketFC');
  }
  public get productTypeFC() {
    return this.globalSection.get('productTypeFC');
  }

  /*
   *API Calls to get region/market/global region
   */
  public getGlobalRegions(): Subscription {
    return this.adminService.getGlobalRegions().subscribe(this.onGlobalRegionsReceived.bind(this));
  }

  public onGlobalRegionsReceived(result): void {
    this.globalRegionList = result;
    if (this.selectedPermissions) {
      this.globalSection.controls.globalRegionFC.setValue(
        result.find(f => f.key === this.selectedPermissions.globalRegionId)
      );
      this.userPermission.globalRegion = result.find(f => f.key === this.selectedPermissions.globalRegionId);
    } else {
      this.selectedGlobalRegion = result[1];
      this.userPermission.globalRegion = result[1];
      this.globalSection.controls.globalRegionFC.setValue(result[1]);
    }
  }

  public getDivision(selectedGlobalRegion: number) {
    this.adminService.getDivisions(selectedGlobalRegion).subscribe(result => {
      this.divisionList = result;
      if (this.selectedPermissions) {
        const firstDivision = this._getGeoOption(this.divisionList, this.selectedPermissions.divisionId);
        this.globalSection.controls.divisionFC.setValue(firstDivision);
        this.userPermission.division = firstDivision;
      } else {
        this.selectedDivision = result[0];
        this.userPermission.division = result[0];
        this.globalSection.controls.divisionFC.setValue(result[0]);
      }
    });
  }
  public getMarkets(selectedRegion: number) {
    this.adminService.getMarkets(selectedRegion).subscribe(result => {
      this.marketList = result;
      if (this.selectedPermissions) {
        const firstMarket = this._getGeoOption(this.marketList, this.selectedPermissions.marketId);
        this.globalSection.controls.marketFC.setValue(firstMarket);
        this.userPermission.market = firstMarket;
      } else {
        this.selectedMarket = result[0];
        this.userPermission.market = result[0];
        this.globalSection.controls.marketFC.setValue(result[0]);
      }
    });
  }

  public getSubMarkets(selectedMarket: number) {
    this.adminService.getSubMarkets(selectedMarket).subscribe(result => {
      this.subMarketList = result;
      if (this.selectedPermissions) {
        const firstSubMarket = this._getGeoOption(this.subMarketList, this.selectedPermissions.subMarketId);
        this.globalSection.controls.submarketFC.setValue(firstSubMarket);
        this.userPermission.submarket = firstSubMarket;
      } else {
        this.selectedSubMarket = result[0];
        this.userPermission.submarket = result[0];
        this.globalSection.controls.submarketFC.setValue(result[0]);
      }
    });
  }

  private _getGeoOption(dataSource: IOption[], targetValue: any): IOption {
    const [firstItem] = dataSource;
    return dataSource.find(option => option.key === targetValue) || firstItem;
  }
}
